import React, { useEffect, useState } from "react"
import { Button, Input, Select, notification } from "antd"
import { useSelector } from "react-redux"
import { Icon } from "components"
import { API, Endpoints } from "utils/api"
import Strings from "utils/strings"

interface SendBackToPackingEditorProps {
	order: any
	setOrders: React.Dispatch<React.SetStateAction<any[]>>
	onBack: () => void
}

const msToTime = (ms: number): string => {
	const h = Math.floor(ms / 3600000)
	const m = Math.floor((ms % 3600000) / 60000)
	return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
}

const timeToMs = (time: string): number => {
	const [h, m] = time.split(":").map(Number)
	return (h * 3600 + m * 60) * 1000
}

const toDateString = (date: Date): string => date.toISOString().slice(0, 10)

const getDefaultDeliveryDate = (
	order: any,
	filterDate: string | undefined
): string => {
	const reference = filterDate
		? toDateString(new Date(filterDate))
		: toDateString(new Date())

	const getNextDay = (d: string): string => {
		const next = new Date(d + "T00:00:00.000Z")
		next.setUTCDate(next.getUTCDate() + 1)
		return toDateString(next)
	}

	const current = order.orderFlow?.deliveryDate
	if (!current) return getNextDay(reference)

	const orderDate = toDateString(new Date(current))

	if (orderDate === reference) return getNextDay(reference)
	if (orderDate < reference) return reference
	return orderDate
}

const SendBackToPackingEditor: React.FC<SendBackToPackingEditorProps> = ({
	order,
	setOrders,
	onBack,
}) => {
	const selectedStatus: string[] =
		useSelector((state: any) => state.ordersStatusFilter) || []
	const filterDate: string | undefined = useSelector(
		(state: any) => state.filters?.date
	)

	const [shifts, setShifts] = useState<any[]>([])
	const [selectedShift, setSelectedShift] = useState<string>(
		order.orderFlow?.batchShift || ""
	)
	const [deliveryDate, setDeliveryDate] = useState<string>(
		getDefaultDeliveryDate(order, filterDate)
	)
	const [slotFrom, setSlotFrom] = useState<string>(
		msToTime(order.slot?.from ?? 0)
	)
	const [slotTo, setSlotTo] = useState<string>(msToTime(order.slot?.to ?? 0))
	const [saving, setSaving] = useState(false)

	useEffect(() => {
		const loadShifts = async () => {
			const contractId = order.contract?._id || order.contract || ""
			try {
				const response = await API.get({
					url: Endpoints.uriShifts(contractId),
				})
				if (response.ok) {
					setShifts(response.data.results?.shifts || [])
				}
			} catch {
				// silently ignore shift loading errors
			}
		}
		loadShifts()
	}, [order.contract])

	const handleSave = async () => {
		if (!selectedShift) {
			return notification.warn({
				message: Strings.sidebar.orders,
				description: Strings.orders.fillBagFields,
				placement: "bottomRight",
				duration: 5,
			})
		}

		setSaving(true)

		const data = {
			slot: {
				...order.slot,
				from: timeToMs(slotFrom),
				to: timeToMs(slotTo),
			},
			orderFlow: {
				...order.orderFlow,
				batchShift: selectedShift,
				deliveryDate: new Date(deliveryDate + "T00:00:00.000Z").toISOString(),
				status: "packing",
			},
			activeStatus: order.orderFlow?.status,
		}

		try {
			const response = await API.put({
				url: Endpoints.uriOrders(order._id),
				data,
			})

			if (response.ok) {
				const selectedDate = filterDate
					? toDateString(new Date(filterDate))
					: null
				const matchesDateFilter = !selectedDate || deliveryDate === selectedDate
				const packingVisible = selectedStatus.includes("packing")
				const shouldKeep = matchesDateFilter && packingVisible

				setOrders((prev) =>
					shouldKeep
						? prev.map((o) =>
								o._id === order._id
									? {
											...o,
											slot: data.slot,
											orderFlow: { ...o.orderFlow, ...data.orderFlow },
										}
									: o
							)
						: prev.filter((o) => o._id !== order._id)
				)

				notification.success({
					message: Strings.sidebar.orders,
					description:
						response?.data?.message || Strings.orders.sendBackToPacking,
					placement: "bottomRight",
					duration: 5,
				})

				onBack()
			} else {
				notification.error({
					message: Strings.sidebar.orders,
					description:
						response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch {
			notification.error({
				message: Strings.sidebar.orders,
				description: Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		} finally {
			setSaving(false)
		}
	}

	return (
		<>
			<div className="OrderDetailPanel__header">
				<div className="OrderDetailPanel__title">
					<Button type="text" icon={<Icon name="back" />} onClick={onBack} />
					<h3>{Strings.orders.sendBackToPacking}</h3>
				</div>
			</div>

			<div className="OrderDetailPanel__content">
				<div className="PackagingEditor__field">
					<label className="InputLabel">{Strings.fields.shift}</label>
					<Select
						style={{ width: "100%" }}
						value={selectedShift || undefined}
						placeholder={Strings.fields.shift}
						onChange={setSelectedShift}
					>
						{shifts.map((s: any) => (
							<Select.Option key={s._id} value={s._id}>
								{s.name}
							</Select.Option>
						))}
					</Select>
				</div>

				<div className="PackagingEditor__field">
					<label className="InputLabel">{Strings.orders.deliveryDate}</label>
					<Input
						type="date"
						value={deliveryDate}
						onChange={(e) => setDeliveryDate(e.target.value)}
					/>
				</div>

				<div className="PackagingEditor__field">
					<label className="InputLabel">{Strings.orders.slot}</label>
					<div style={{ display: "flex", gap: 8 }}>
						<Input
							type="time"
							value={slotFrom}
							style={{ width: "50%" }}
							onChange={(e) => setSlotFrom(e.target.value)}
						/>
						<Input
							type="time"
							value={slotTo}
							style={{ width: "50%" }}
							onChange={(e) => setSlotTo(e.target.value)}
						/>
					</div>
				</div>

				<div className="PackagingEditor__footer">
					<Button type="primary" block loading={saving} onClick={handleSave}>
						{Strings.generic.save}
					</Button>
				</div>
			</div>
		</>
	)
}

export default SendBackToPackingEditor
